import { map } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import FormCheckbox from "../../components/form/FormCheckbox";
import FormDropdown from "../../components/form/FormDropdown";
import FormIconPicker from "../../components/form/FormIconPicker";
import FormTextField from "../../components/form/FormTextField";
import ModalBody from "../../components/modal/ModalBody";
import ModalCheckboxGrid from "../../components/modal/ModalCheckboxGrid";
import ModalField from "../../components/modal/ModalField";
import ModalFooter from "../../components/modal/ModalFooter";
import ModalFooterLeftGroup from "../../components/modal/ModalFooterLeftGroup";
import ModalFooterRightGroup from "../../components/modal/ModalFooterRightGroup";
import ModalGroup from "../../components/modal/ModalGroup";
// import "./exportCsv.scss";
import ModalHeading from "../../components/modal/ModalHeading";
import ModalRow from "../../components/modal/ModalRow";
import ModalView from "../../components/modal/ModalView";
import PubSubTopics from "../../constants/pubSubTopics";
import templateIcons from "../../constants/templateIcons";
import { typeSelectors } from "../../modules/definition/types";
import { uiStateOperations } from "../../modules/uiState";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import templates from "../../templates";
import { immutableRecords } from "../../types";
import { listToMap } from "../../utils/immutableExtensions";
import selectors from "./selectors";

const CUSTOM_TEMPLATES_ID = "custom";

const categoryOptions = [
  ...map(templates, t => {
    return {
      value: t.id,
      label: t.label
    };
  }),
  {
    value: CUSTOM_TEMPLATES_ID,
    label: "Custom"
  }
];

const iconOptions = map(templateIcons, i => {
  return { color: "iconMono", icon: i };
});

class SaveTemplateModal extends PureComponent {
  constructor(props) {
    super(props);

    this.onSave = this.onSave.bind(this);

    this.state = {
      typesCheck: listToMap(props.itemTypes).map(_t => false),
      relationshipVisibleColumnsCheck: listToMap(props.showAsColumnTypes).map(t =>
        Boolean(props.columnIsVisibleTypes.find(c => c.id === t.id))
      ),
      retainCalendarMarkers: false,
      retainTimelineRange: false,
      retainSavedFilters: false,
      name: "",
      creator: "",
      description: "",
      category: CUSTOM_TEMPLATES_ID,
      icon: "custom"
    };
  }

  componentDidMount() {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.SAVE_TEMPLATE_OPENED
    });
    this._pubSubEscapeKeyDownToken = PubSub.subscribe(PubSubTopics.escapeKeyDown, this.props.onCloseClick);
  }

  componentWillUnmount() {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.SAVE_TEMPLATE_CLOSED
    });
    PubSub.unsubscribe(this._pubSubEscapeKeyDownToken);
  }

  getItemTypeCheckBoxes() {
    const { typesCheck } = this.state;
    return this.props.itemTypes
      .map(t => (
        <FormCheckbox
          key={t.id}
          checkboxState={typesCheck.get(t.id)}
          label={t.label}
          onChange={newState => this.setState({ typesCheck: typesCheck.set(t.id, newState) })}
        />
      ))
      .toList()
      .toArray();
  }

  getRelationshipColumnCheckBoxes() {
    const { relationshipVisibleColumnsCheck } = this.state;
    return this.props.showAsColumnTypes
      .map(t => (
        <FormCheckbox
          key={t.id}
          checkboxState={relationshipVisibleColumnsCheck.get(t.id)}
          label={t.label}
          onChange={newState =>
            this.setState({ relationshipVisibleColumnsCheck: relationshipVisibleColumnsCheck.set(t.id, newState) })
          }
        />
      ))
      .toList()
      .toArray();
  }

  onSave() {
    const name = this.state.name;
    const typeIds = this.state.typesCheck
      .filter(t => t)
      .map((t, id) => id)
      .toList();
    const relationshipVisibleColumnIds = this.state.relationshipVisibleColumnsCheck
      .filter(t => t)
      .map((t, id) => id)
      .toList();

    const templateData = selectors.getTemplateToSave(
      this.props.localStore,
      typeIds,
      this.state.retainCalendarMarkers,
      this.state.retainTimelineRange,
      this.state.retainSavedFilters,
      relationshipVisibleColumnIds,
      name,
      this.state.creator,
      this.state.category,
      this.state.icon,
      this.state.description
    );
    const json = JSON.stringify(templateData);

    let nativeMsg = {
      msg: nativeCommsBroadcastMessageTypes.SAVE_CUSTOM_TEMPLATE_DATA,
      name,
      json
    };
    PubSub.publish(PubSubTopics.sendNativeMessage, nativeMsg);
    this.props.onCloseClick();
  }

  render() {
    const modalHeading = (
      <ModalHeading
        title="Save as Template"
        showCancel={true}
        onCancelClick={this.props.onCloseClick}
        showDone={true}
      />
    );
    const modalFooter = (
      <ModalFooter>
        <ModalFooterLeftGroup>
          <FormButton primary={true} label={`Cancel`} onClick={this.props.onCloseClick} />
        </ModalFooterLeftGroup>
        <ModalFooterRightGroup>
          <FormButton primary={true} label={`Save`} onClick={this.onSave} disabled={!this.state.name} />
        </ModalFooterRightGroup>
      </ModalFooter>
    );

    const bodyContent = (
      <Fragment>
        <ModalGroup label="Details">
          <ModalRow>
            <ModalField label="Name">
              <FormTextField
                value={this.state.name}
                placeholder="Name of template"
                onChange={value => this.setState({ name: value })}
              />
            </ModalField>
            <ModalField label="Creator">
              <FormTextField
                value={this.state.creator}
                placeholder="Your name"
                onChange={value => this.setState({ creator: value })}
              />
            </ModalField>
          </ModalRow>
          <ModalRow>
            <ModalField label="Category">
              <FormDropdown
                options={categoryOptions}
                value={this.state.category}
                onChange={value => this.setState({ category: value })}
                title="Category"
              />
            </ModalField>
            <ModalField label="Icon">
              <FormIconPicker
                value={{ icon: this.state.icon, color: "iconMono" }}
                options={iconOptions}
                id="saveTemplateIconPicker"
                onChange={opt => this.setState({ icon: opt.icon })}
                largeIcons={true}
              />
            </ModalField>
          </ModalRow>
          <ModalRow>
            <ModalField label="Description">
              <FormTextField
                multiline={true}
                value={this.state.description}
                onChange={value => this.setState({ description: value })}
              />
            </ModalField>
          </ModalRow>
        </ModalGroup>
        <ModalGroup label="Include existing items for types">
          <ModalRow>
            <ModalField>
              <ModalCheckboxGrid columns={3}>{this.getItemTypeCheckBoxes()}</ModalCheckboxGrid>
            </ModalField>
          </ModalRow>
        </ModalGroup>
        <ModalGroup label="Include other existing data">
          <ModalRow>
            <ModalField>
              <ModalCheckboxGrid columns={3}>
                <FormCheckbox
                  checkboxState={this.state.retainCalendarMarkers}
                  label="Calendar Markers"
                  onChange={state => this.setState({ retainCalendarMarkers: state })}
                />
                <FormCheckbox
                  checkboxState={this.state.retainTimelineRange}
                  label="Timeline Range"
                  onChange={state => this.setState({ retainTimelineRange: state })}
                />
                <FormCheckbox
                  checkboxState={this.state.retainSavedFilters}
                  label="Saved Filters"
                  onChange={state => this.setState({ retainSavedFilters: state })}
                />
              </ModalCheckboxGrid>
            </ModalField>
          </ModalRow>
        </ModalGroup>
        <ModalGroup label="Show relationship columns by default">
          <ModalRow>
            <ModalField>
              <ModalCheckboxGrid columns={3}>{this.getRelationshipColumnCheckBoxes()}</ModalCheckboxGrid>
            </ModalField>
          </ModalRow>
        </ModalGroup>
      </Fragment>
    );

    return (
      <ModalView header={modalHeading} footer={modalFooter} fixedHeight={null}>
        <ModalBody overflowScroll={true}>{bodyContent}</ModalBody>
      </ModalView>
    );
  }
}

SaveTemplateModal.propTypes = {
  localStore: PropTypes.instanceOf(immutableRecords.TimelineRecord).isRequired,
  itemTypes: ImmutablePropTypes.list.isRequired,
  showAsColumnTypes: ImmutablePropTypes.list.isRequired,
  columnIsVisibleTypes: ImmutablePropTypes.list.isRequired,
  onCloseClick: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  return {
    localStore: state.present,
    itemTypes: typeSelectors.getOrderedTypes(state.present),
    showAsColumnTypes: typeSelectors.getShowAsColumnTypes(state.present),
    columnIsVisibleTypes: typeSelectors.getColumnIsVisibleTypes(state.present)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    onCloseClick: () => {
      dispatch(uiStateOperations.hideSaveTemplateModal());
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveTemplateModal);
